<template>
  <page>
    <page-header-new
      :title="$t('AnalyticsCampaignsPage_Title')"
      :breadcrumbs="breadcrumbs"
    >
      <template v-slot:actions>
        <channel-select :platform="true" @change="updateParams()" />
        <date-filter @change="updateParams($event)" />
      </template>
    </page-header-new>

    <page-content>
      <campaigns-chart
        :project-id="$route.query.project_id"
        :start-date="startDate"
        :end-date="endDate"
        :items="chartItems"
        class="chart-ct"
        :class="{ hidden: !showGraph }"
        export-table-method="campaigns_list"
      >
        <expandable-control
          class="expandable-el-card"
          :open-text="$t('AnalyticsCampaignsPage_Graph_Show')"
          :close-text="$t('AnalyticsCampaignsPage_Graph_Hide')"
          @toggle="toggleGraph"
        />
      </campaigns-chart>
      <el-tag
        v-if="isChannelSelectEnabled"
        effect="plain"
        class="m-bottom-10"
        size="mini"
      >
        {{ $t('AnalyticsCampaignsPage_Tag_CampaignsFilteredAbove') }}
      </el-tag>
      <rest-list
        :key="key"
        :top-data="topData"
        :payload="fetchEndpoint"
        :watch-payload="false"
        :after-fetch="afterFetch"
        :max-height="pageHeight"
        @dataLoaded="setData($event)"
        @selection-change="selectionChange($event)"
        ref="list"
      >
        <template v-if="data[0]">
          <el-table-column type="selection" align="center" />
          <rest-list-column
            prop="title"
            :title="$t('AnalyticsCampaignsPage_Table_CampaignName')"
            :width="300"
            :fixed="true"
            tooltip
          >
            <template v-slot:default="scope">
              <el-tooltip
                :content="scope.rowValue(scope)"
                :open-delay="600"
                class="item"
                effect="dark"
                placement="bottom"
              >
                <div class="campaign-column">
                  <router-link :to="getCampaignLink(scope.row)">
                    {{ scope.rowValue(scope) }}
                  </router-link>
                  <div>
                    <el-tag
                      v-if="scope.row.active"
                      size="mini"
                      type="success"
                      effect="plain"
                      class="tag"
                    >
                      {{ $t('AnalyticsCampaignsPage_Tag_Live') }}
                    </el-tag>
                  </div>
                </div>
              </el-tooltip>
            </template>
          </rest-list-column>
          <rest-list-column-enum
            prop="type"
            :title="$t('AnalyticsCampaignsPage_Table_Type')"
            :width="250"
            :allow-multiple="true"
            :enum="campaignTriggerTypeLabels"
            :clearable="false"
          />
          <rest-list-column-enum
            prop="channels"
            :title="$t('AnalyticsCampaignsPage_Table_Channels')"
            :width="250"
            :allow-multiple="true"
            :enum="channelEnum"
            :clearable="false"
            tooltip
          />
          <rest-metric-group
            v-for="(metricGroup, metricIndex) in metricGroups"
            :key="metricIndex"
            :metric-group="metricGroup"
            :prop="'metricGroups[' + metricIndex + ']'"
            align="left"
            :width="metricIndex !== '0' ? 105 : 200"
          />
        </template>
      </rest-list>
    </page-content>
  </page>
</template>

<script>
import Page from '@/common/components/page/Page'
import PageContent from '@/common/components/page/PageContent'
import DateFilter from '@/common/components/dashboard/DateFilter'
import PageHeaderNew from '@/common/components/page/PageHeaderNew'
import moment from 'moment'
import CampaignsChart from '@/modules/analytics/components/CampaignsChart'
import {
  projectEndpoint,
  projectRoute,
  getDateFilterRange
} from '@/core/helpers'
import RestMetricGroup from '@/common/components/rest/RestMetricGroup'
import RestList from '@/common/components/rest/RestList'
import RestListColumn from '@/common/components/rest/RestListColumn'
import { campaignTriggerTypeLabels } from '@/modules/analytics/enum/CampaignTypeLabels'
import RestListColumnEnum from '@/common/components/rest/RestListColumnEnum'
import ExpandableControl from '../components/ExpandableControl'
import { channelLabels } from '@/modules/analytics/enum/ChannelLabels'
import _ from 'lodash'
import ChannelSelect from '@/common/components/analytics/ChannelSelect'
import { mapGetters } from 'vuex'

export default {
  name: 'AnalyticsCampaignsPage',

  components: {
    ChannelSelect,
    RestListColumnEnum,
    RestListColumn,
    RestList,
    RestMetricGroup,
    CampaignsChart,
    PageHeaderNew,
    DateFilter,
    PageContent,
    Page,
    ExpandableControl
  },

  data() {
    return {
      metricGroups: {},
      key: '',
      topData: [],
      data: {},
      chartItems: {},
      selectedItems: [],
      startDate: '',
      endDate: '',
      skipFirstChange: false,
      showGraph: false,
      campaignTriggerTypeLabels,
      channelLabels,
      isChannelSelectEnabled: false
    }
  },

  created() {
    let dateRange = getDateFilterRange()
    this.startDate = moment(dateRange.start).format('YYYY-MM-DD')
    this.endDate = moment(dateRange.end).format('YYYY-MM-DD')
  },

  methods: {
    toggleGraph(isOpen) {
      this.showGraph = isOpen
    },

    afterFetch() {
      this.$nextTick(_ => {
        this.selectionChange(this.selectedItems)
      })
    },

    updateParams(extraParams) {
      if (extraParams) {
        this.startDate = moment(extraParams.start).format('YYYY-MM-DD')
        this.endDate = moment(extraParams.end).format('YYYY-MM-DD')
      }
      this.data = {}
      this.key = new Date().getTime() // refresh the table items
    },

    setData(data) {
      this.topData = []
      this.data = Object.assign({}, this.data, data)
      this.isChannelSelectEnabled = false
      const availableChannels = Object.keys(this.selectedChannels)
      const selectedChannels = availableChannels.filter(
        ch => this.selectedChannels[ch]
      )
      const dataMetricGroups = this.data[0] ? this.data[0].metricGroups : []
      if (
        selectedChannels.length &&
        availableChannels.length !== selectedChannels.length
      ) {
        this.isChannelSelectEnabled = true
        this.metricGroups = dataMetricGroups.reduce((groups, item, index) => {
          let name = this.userConfig.show_channel_platforms
            ? availableChannels.filter(ch => item.name.includes(ch))[0] ||
              item.name
            : item.name

          if (name && this.userConfig.channels[name] !== false) {
            groups[index] = item
          }
          return groups
        }, {})
      } else {
        this.metricGroups = [...dataMetricGroups]
      }
      if (this.selectedItems.length) {
        this.selectedItems.forEach((val, index) => {
          // Updating selected items and placing at the top of the table
          let updatedSelectedItem = data.find(item => item.id === val.id)
          if (updatedSelectedItem) {
            this.selectedItems[index] = updatedSelectedItem
          }
        })
        this.topData = this.selectedItems
        this.skipFirstChange = true
      }
    },

    selectionChange(items) {
      // filter triggers selectionChange, so skip it for the first time and select the previous elements
      if (this.skipFirstChange) {
        this.skipFirstChange = false
        this.toggleSelection(this.selectedItems)
      } else {
        this.selectedItems = items
        this.chartItems = items.reduce((acc, item) => {
          acc[item.id] = { id: item.id, title: item.title }
          return acc
        }, {})
      }
    },

    toggleSelection(rows) {
      if (rows) {
        rows.forEach(row => {
          this.$refs.list.$refs['el-table'].toggleRowSelection(row)
        })
      } else {
        this.$refs.list.$refs['el-table'].clearSelection()
      }
    },

    getCampaignLink(row) {
      return projectRoute({
        name: 'CampaignOverview',
        query: { id: row.id }
      })
    }
  },

  computed: {
    ...mapGetters(['userConfig']),

    breadcrumbs() {
      return [
        {
          label: this.$t('Analytics_Breadcrumb_Analytics')
        },
        {
          label: this.$t('Analytics_Breadcrumb_Campaigns'),
          route: projectRoute({ name: 'AnalyticsCampaigns' })
        }
      ]
    },

    fetchEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: `analytics/campaigns`,
        params: {
          date_from: this.startDate,
          date_to: this.endDate,
          platforms: this.userConfig.show_channel_platforms,
          channel_select: Object.keys(this.selectedChannels)
            .filter(ch => this.selectedChannels[ch])
            .join(',')
        }
      })
    },

    pageHeight() {
      if ($('.page').height() > 500) {
        return $('.page').height() - 40
      }
      return 500
    },

    channelEnum() {
      return _.pickBy(channelLabels, (value, key) => {
        switch (key) {
          case 'mobile_push':
            return (
              this.$store.state.app.company_channels['ios_push'] ||
              this.$store.state.app.company_channels['android_push']
            )
          default:
            return this.$store.state.app.company_channels[key]
        }
      })
    },

    selectedChannels() {
      return this.userConfig.channels || {}
    }
  }
}
</script>

<style scoped lang="scss">
/deep/ .el-table--border td:first-child .cell {
  padding-left: 14px;
}
.campaign-column {
  display: flex;
  a {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }
  .tag {
    margin-left: 5px;
  }
}

.chart-ct {
  /deep/ .graphWrapper {
    height: 330px;
  }
}
</style>



// WEBPACK FOOTER //
// src/modules/analytics/pages/AnalyticsCampaignsPage.vue